import { Button, Input, Media, Row } from 'reactstrap';

import { Image } from '../../../../../coreComponents/Image';
import { UserRole } from '../../../../../types/Forms';
import { useEffect, useRef, useState } from 'react';
import { isImageFile, MAX_IMAGE_BYTES } from '../../../../../lib/filesUtils';

type UserFormHeadProps = {
  name: string;
  photo: string | null;
  role: UserRole;
  /** Called when user picks a new avatar (valid image). Send this file to backend. */
  onAvatarSelected?: (photo: File) => void;
};

export const UserFormHead = ({ name, photo, role, onAvatarSelected }: UserFormHeadProps) => {
  const [userAvatar, setUserAvatar] = useState('/assets/images/avtar/default-user.png');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [objectURL, setObjectURL] = useState<string | null>(null);

  // initial photo from BE
  useEffect(() => {
    const hasPhoto =
      typeof photo === 'string' && photo.trim() !== '' && !photo.includes('default-user.png');

    setUserAvatar(
      hasPhoto
        ? `${process.env.REACT_APP_BACKEND_STATIC}/userPics/${photo}`
        : '/assets/images/avtar/default-user.png',
    );
  }, [photo]);

  // cleanup object URLs
  useEffect(() => {
    return () => {
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [objectURL]);

  const openPicker = () => fileRef.current?.click();

  const onFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (!isImageFile(f)) {
      console.warn(`${f.name}: not an image`);
      e.currentTarget.value = '';
      return;
    }
    if (f.size > MAX_IMAGE_BYTES) {
      console.warn(`${f.name}: too large (max 10MB)`);
      e.currentTarget.value = '';
      return;
    }

    // preview immediately
    const url = URL.createObjectURL(f);
    setUserAvatar(url);
    if (objectURL) URL.revokeObjectURL(objectURL);
    setObjectURL(url);

    onAvatarSelected?.(f); // pass file to parent (upload to BE)
  };

  return (
    <Row className="mb-2">
      <div className="profile-title">
        <Media className="d-flex">
          <div className="position-relative">
            <Image
              className="rounded-circle position-relative"
              alt=""
              src={userAvatar}
              style={{ width: 70, height: 70, objectFit: 'cover' }}
            />

            <Input innerRef={fileRef} type="file" accept="image/*" onChange={onFileChange} hidden />

            <Button
              type="button"
              onClick={openPicker}
              className="position-absolute bottom-0 end-0 m-0 p-2 d-flex align-items-center justify-content-center border rounded-circle"
              color="white"
              style={{ width: 24, height: 24 }}
              title="Change avatar"
              aria-label="Change avatar"
            >
              <i className="icofont icofont-pencil-alt-5 p-0 text-muted" />
            </Button>
          </div>

          <Media body className="flex-grow-1">
            <h3 className="mb-1 f-w-600">{name}</h3>
            <p>{role}</p>
          </Media>
        </Media>
      </div>
    </Row>
  );
};
